/**
 * ProviderDashboardPage — summary for a logged-in provider.
 * Shows: queue counters (scoped to self), recent assigned ops, recent docs, WS connection.
 * No financial figures.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Inbox, Truck, Ticket, FileText, AlertTriangle, RefreshCw, Archive } from 'lucide-react';
import { fetchQueues, listOperations, listDocuments } from '@/services/workflowApi';
import { fetchWithFallback } from '@/services/resilientCache';
import StateBadge from '@/components/workflow/StateBadge';
import { queueLabelEs, opTypeLabelEs, docVerificationLabelEs } from '@/components/workflow/labels';
import UsdtRatesEditor from '@/components/wallets/UsdtRatesEditor';

const ProviderDashboardPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { subscribe } = useRealtime();
  const navigate = useNavigate();
  const [queues, setQueues] = useState({});
  const [counts, setCounts] = useState({});
  const [recentOps, setRecentOps] = useState([]);
  const [recentDocs, setRecentDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staleAge, setStaleAge] = useState(0); // ms since cached snapshot, 0 = fresh

  const load = useCallback(async () => {
    setLoading(true);
    const uid = user?.id;
    const [q, ops, docs] = await Promise.all([
      fetchWithFallback('provider:queues', uid, () => fetchQueues(getAuthHeaders)),
      // Cache the FULL list under 'provider:operations' so the dedicated
      // /operaciones page can render from cache on a cold offline visit. We
      // just slice the first 5 here for the dashboard preview.
      fetchWithFallback('provider:operations', uid, () => listOperations(getAuthHeaders, { limit: 200 })),
      fetchWithFallback('provider:documents', uid, () => listDocuments(getAuthHeaders, {})),
    ]);
    setQueues(q.data?.queues || {});
    setCounts(q.data?.counts || {});
    setRecentOps((ops.data?.operations || []).slice(0, 5));
    setRecentDocs((docs.data?.documents || []).slice(0, 5));
    const fromCacheAges = [q, ops, docs].filter((r) => r.fromCache).map((r) => r.age);
    setStaleAge(fromCacheAges.length ? Math.max(...fromCacheAges) : 0);
    setLoading(false);
  }, [getAuthHeaders, user?.id]);

  useEffect(() => { load(); }, [load]);

  // Realtime hints: any operation/document event triggers a refresh.
  useEffect(() => {
    const offs = [
      subscribe('operation.assigned', load),
      subscribe('operation.transition', load),
      subscribe('document.uploaded', load),
      subscribe('document.verified', load),
    ];
    return () => offs.forEach((f) => f && f());
  }, [subscribe, load]);

  // Auto-refetch when connectivity is restored (offline -> online).
  useEffect(() => {
    const onOnline = () => load();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [load]);

  const isPa = user?.role === 'payment_agent';
  // Highlight queues relevant to this role.
  const myQueues = Object.entries(queues).filter(([, spec]) => {
    if (isPa) return spec.op_type === 'remittance';
    return spec.op_type === 'ticket';
  });

  return (
    <div className="space-y-6" data-testid="provider-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Panel</h1>
          <p className="text-xs text-slate-500">Operaciones asignadas y tu cola de trabajo</p>
        </div>
        <div className="flex items-center gap-2">
          {staleAge > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 ring-1 ring-amber-300 px-2 py-0.5 rounded-full" data-testid="dashboard-cache-badge">
              <Archive className="w-3 h-3" />
              cache · hace {Math.round(staleAge / 1000)}s
            </span>
          )}
          <Button size="sm" variant="outline" onClick={load} data-testid="provider-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refrescar
          </Button>
        </div>
      </div>

      {user?.force_password_change && (
        <Card className="p-4 bg-amber-50 border-amber-300 flex items-center gap-3" data-testid="force-pwd-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-amber-900">Cambia tu contraseña</p>
            <p className="text-xs text-amber-700">Por seguridad, debes definir una nueva contraseña antes de continuar.</p>
          </div>
          <Button size="sm" onClick={() => navigate('/provider/cuenta')} className="bg-amber-600 hover:bg-amber-700">Ir a Mi cuenta</Button>
        </Card>
      )}

      {/* USDT rates — visible solo para el proveedor CU. */}
      {user?.role === 'payment_agent' && (user?.country || '').toUpperCase() === 'CU' && (
        <UsdtRatesEditor />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {myQueues.map(([name, spec]) => (
          <button
            key={name}
            onClick={() => navigate('/provider/queues')}
            data-testid={`my-queue-${name}`}
            className="text-left p-4 bg-white rounded-lg border border-slate-200 hover:border-blue-400 hover:shadow-sm transition-all"
          >
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{opTypeLabelEs(spec.op_type)}</p>
            <p className="text-sm font-bold text-slate-900 mt-0.5">{queueLabelEs(name)}</p>
            <div className="flex items-center justify-between mt-3">
              <span className="text-3xl font-bold text-blue-600">{counts[name] ?? 0}</span>
              <Inbox className="w-5 h-5 text-slate-400" />
            </div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              {isPa ? <Truck className="w-4 h-4 text-blue-600" /> : <Ticket className="w-4 h-4 text-blue-600" />}
              Operaciones recientes
            </h2>
            <Button size="sm" variant="outline" onClick={() => navigate('/provider/operaciones')} className="text-xs">Ver todas</Button>
          </div>
          {loading ? <p className="text-xs text-slate-400">Cargando…</p> :
           recentOps.length === 0 ? <p className="text-xs text-slate-500 italic">Sin operaciones asignadas</p> :
            <div className="space-y-2">
              {recentOps.map((o) => (
                <div key={o.id} className="flex items-center gap-2 p-2 border border-slate-100 rounded hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/provider/operaciones?op=${o.id}`)} data-testid={`recent-op-${o.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono text-slate-400">{o.id.slice(0, 8)}…</p>
                    <p className="text-xs text-slate-700">{o.route_origin && o.route_destination ? `${o.route_origin} → ${o.route_destination}` : '—'}</p>
                  </div>
                  <StateBadge state={o.state} />
                </div>
              ))}
            </div>
          }
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              Documentos recientes
            </h2>
            <Button size="sm" variant="outline" onClick={() => navigate('/provider/documentos')} className="text-xs">Ver todos</Button>
          </div>
          {loading ? <p className="text-xs text-slate-400">Cargando…</p> :
           recentDocs.length === 0 ? <p className="text-xs text-slate-500 italic">Sin documentos</p> :
            <div className="space-y-2">
              {recentDocs.map((d) => (
                <div key={d.id} className="flex items-center gap-2 p-2 border border-slate-100 rounded" data-testid={`recent-doc-${d.id}`}>
                  <FileText className="w-4 h-4 text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{d.filename}</p>
                    <p className="text-[10px] text-slate-500">{d.document_type}</p>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ring-1 ring-inset ${
                    d.verification_status === 'VERIFIED' ? 'bg-emerald-50 text-emerald-700 ring-emerald-300'
                    : d.verification_status === 'REJECTED' ? 'bg-rose-50 text-rose-700 ring-rose-300'
                    : 'bg-amber-50 text-amber-700 ring-amber-300'
                  }`}>{docVerificationLabelEs(d.verification_status)}</span>
                </div>
              ))}
            </div>
          }
        </Card>
      </div>
    </div>
  );
};

export default ProviderDashboardPage;
